import React from 'react'
import { Navigate, createBrowserRouter, RouterProvider } from 'react-router-dom'

import { AlertProvider } from '../components/AlertContext'
import { useAuth } from '../components/AuthContext'
import { PlatformAuth } from '../components/algolia'
import { ProductSync } from '../components/bigcommerce/productSync'
import { Loader } from '../components/common/loader'
import { MaintenanceMode } from '../components/common/maintenanceMode'
import { NotFound404 } from '../components/common/notFound404'
import { Login } from '../components/login'
import { useAppexRedesignFlag } from '../featureFlags'

import { bigCommerceRoutes } from './bigCommerceRoutes'
import { commonRoutes } from './commonRoutes'
import { RequireAuth } from './requireAuth'

const Admin = React.lazy(
    (): any =>
        import(
            /* webpackChunkName: "admin" */
            '../components/admin'
        )
)

// Code-split so Satellite v2 + all redesign screens ship only to users who get
// the flag; the default bundle stays on the legacy UI.
const RedesignApp = React.lazy(
    (): any =>
        import(
            /* webpackChunkName: "redesign" */
            '../redesign/RedesignApp'
        )
)

export const Router: React.FunctionComponent = () => {
    const { isAdmin, isPlatformAuthed, setIsPlatformAuthed } = useAuth()
    const redesignEnabled = useAppexRedesignFlag()
    let router = createBrowserRouter([
        {
            path: '/',
            element: <Navigate to={'/integrations'} replace />,
        },

        ...commonRoutes(),
        ...bigCommerceRoutes(),

        {
            path: '/admin/*',
            element: isAdmin ? (
                <RequireAuth>
                    <Admin />
                </RequireAuth>
            ) : (
                <Navigate to={'/*'} replace />
            ),
        },

        /* route used only by platform algolia authentication callback */
        {
            path: '/auth',
            element: (
                <AlertProvider>
                    <PlatformAuth
                        isPlatformAuthed={isPlatformAuthed}
                        setIsPlatformAuthed={setIsPlatformAuthed}
                        cookieAllowed={Boolean(navigator.cookieEnabled)}
                    />
                </AlertProvider>
            ),
        },

        {
            path: '/login',
            element: <Login />,
        },

        {
            path: '/maintenance-mode',
            element: <MaintenanceMode />,
        },

        {
            path: '/bigcommerce/product-sync',
            element: <ProductSync />,
        },

        /* Since the 404 route is a wildcard, it should be last */
        {
            path: '*',
            element: <NotFound404 />,
        },
    ])

    // Redesign gate: when the flag is on, the redesigned dashboard owns every
    // authed path (auth/login/maintenance/admin are preserved). Flag off → the
    // legacy routes above are used unchanged.
    if (redesignEnabled) {
        router = createBrowserRouter([
            {
                path: '/auth',
                element: (
                    <AlertProvider>
                        <PlatformAuth
                            isPlatformAuthed={isPlatformAuthed}
                            setIsPlatformAuthed={setIsPlatformAuthed}
                            cookieAllowed={Boolean(navigator.cookieEnabled)}
                        />
                    </AlertProvider>
                ),
            },
            {
                path: '/login',
                element: <Login />,
            },
            {
                path: '/maintenance-mode',
                element: <MaintenanceMode />,
            },
            {
                path: '/bigcommerce/product-sync',
                element: <ProductSync />,
            },
            {
                path: '/admin/*',
                element: isAdmin ? (
                    <RequireAuth>
                        <Admin />
                    </RequireAuth>
                ) : (
                    <Navigate to={'/*'} replace />
                ),
            },
            {
                path: '*',
                element: (
                    <RequireAuth bare>
                        <React.Suspense fallback={<Loader />}>
                            <RedesignApp />
                        </React.Suspense>
                    </RequireAuth>
                ),
            },
        ])
    }

    if (import.meta.env.VITE_MAINTENANCE_MODE === 'true') {
        router = createBrowserRouter([
            {
                path: '/*',
                element: <MaintenanceMode />,
            },
        ])
    }
    return <RouterProvider router={router} />
}
